import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { yupResolver } from '@hookform/resolvers/yup';
import { ModalFooter, ModalFormSection } from 'components/common/modals';
import { ButtonV2 } from 'components/common/ui';
import { FieldV2 } from 'components/common/form-controls';

import serviceVehiclesB2BValidationSchema from './serviceVehiclesB2BValidationSchema';
import styles from './serviceVehiclesB2BForm.style';

const ServiceVehiclesB2BForm = ({
  defaultValues = {},
  isLoading,
  enums,
  onSubmit,
}) => {
  const resolver = yupResolver(serviceVehiclesB2BValidationSchema);

  const methods = useForm({
    defaultValues,
    resolver,
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const handleReset = () => {
    methods.reset();
  };
  const brand = methods.watch('brand');
  const model = methods.watch('model');

  const models = enums?.brands?.find((b) => b.value === brand)?.models || [];
  const months = Array.from({ length: 12 }, (_, i) => {
    const month = format(new Date(2000, i, 1), 'MMMM');
    return {
      value: month,
      label: month,
    };
  });

  useEffect(() => {
    if (!models.find((m) => m.value === model)) methods.setValue('model', '');
  }, [models, model]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalFormSection>
          <Box sx={styles.row}>
            <FieldV2
              name="brand"
              disabled={isLoading}
              isRequired
              control={control}
              type="dropdown"
              label="Brand"
              options={enums?.brands || []}
            />
            <FieldV2
              name="model"
              disabled={!brand || isLoading}
              isRequired
              control={control}
              label="Model"
              type="dropdown"
              options={models || []}
            />
            <FieldV2
              name="edition"
              disabled={isLoading}
              isRequired
              control={control}
              label="Edition"
            />
            <FieldV2
              name="plate"
              disabled={isLoading}
              isRequired
              control={control}
              label="Plate"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              name="estimatedMonthOfDelivery"
              disabled={isLoading}
              isRequired
              control={control}
              type="dropdown"
              options={months || []}
              label="Estimated Month Of Delivery"
            />
            <FieldV2
              name="displacement"
              disabled={isLoading}
              isRequired
              control={control}
              label="Displacement"
              type="number"
            />
            <FieldV2
              name="powerHp"
              disabled={isLoading}
              isRequired
              control={control}
              label="Power (HP)"
              type="number"
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              name="mileageFromOdometer"
              isRequired
              disabled={isLoading}
              control={control}
              label="Mileage (mi)"
              type="number"
            />
            <FieldV2
              name="transmission"
              isRequired
              disabled={isLoading}
              control={control}
              type="dropdown"
              options={enums?.transmissions || []}
              label="Transmission"
            />
            <FieldV2
              name="fuelType"
              isRequired
              disabled={isLoading}
              control={control}
              label="Fuel Type"
              type="dropdown"
              options={enums?.fuelTypes || []}
            />
          </Box>
          <Box sx={styles.row}>
            <FieldV2
              name="year"
              isRequired
              disabled={isLoading}
              control={control}
              label="Year"
              type="number"
            />
            <FieldV2
              name="color"
              isRequired
              disabled={isLoading}
              control={control}
              type="dropdown"
              label="Color"
              options={enums?.colors || []}
            />
            <FieldV2
              name="price"
              isRequired
              disabled={isLoading}
              control={control}
              label="Price"
              type="number"
            />
          </Box>
        </ModalFormSection>

        <ModalFooter>
          <ButtonV2
            type="button"
            disabled={isSubmitting || isLoading}
            variant="outlined"
            onClick={handleReset}
          >
            Reset all
          </ButtonV2>
          <ButtonV2 type="submit" disabled={isSubmitting || isLoading}>
            Save
          </ButtonV2>
        </ModalFooter>
      </form>
    </FormProvider>
  );
};

export default ServiceVehiclesB2BForm;
